import * as React from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { LABEL_MM, type BottleType } from "@/lib/label-canvas/schema"
import type { ArtworkStyleKey, StudioAi } from "@/types/inertia"
import {
  ArtworkRequestError,
  PAINT_FAILED,
  createArtwork,
  pollArtwork,
  type ArtworkError,
  type ArtworkOption,
} from "./artwork-api"
import { StudioSheet } from "./StudioSheet"

// "Describe your design" (M5): the visitor picks a style, describes the
// artwork and (optionally) the wording; our studio paints two options in the
// background (POST /artwork, then GET /artwork/:id every two seconds) and
// "Use this" puts one on the label edge to edge with the wording as editable
// text (the editor does that part: onUse).
//
// One component, four phases: form → painting → results, or error. The state
// lives here rather than in the sheet's content, and this component stays
// mounted while the sheet is closed, so a visitor can close it mid-painting
// and find the painting (or the results) waiting when they reopen it.
//
// The model paints artwork only; the wording never reaches it (decision 1).
// Visitor-facing copy says "we'll paint" / "our studio paints", never "AI".

const DESCRIPTION_MAX = 300
const DESCRIPTION_MIN = 3
/** Short, so that seeded wording fits one line (see SEEDED in LabelCanvas). */
const NAME_MAX = 32
const LINE_MAX = 48
const SLOW_AFTER_MS = 60_000

const COPY = {
  title: "Describe your design",
  lede: "Tell us the feeling and we'll paint the artwork. You add the wording.",
  placeholder: "A vineyard at dusk, vines in gold on burgundy, engraved",
  wordingHint: "Optional. Each becomes wording you can edit on the label; leave both blank for the artwork alone.",
  tooShort: "Please describe your design in a few words.",
  painting: "Painting your artwork… usually 20 to 40 seconds",
  slow: "Taking longer than usual — you can keep waiting or try again",
  results: "Choose your artwork",
  ready: "Your two artwork options are ready.",
  replaces: "This replaces your current label. Undo brings it back.",
  useFailed: "That artwork could not be placed on your label. Please try again.",
  smallPrint: "Every mock-up is subject to graphic design approval by our team.",
} as const

/** Codes a second attempt cannot fix straight away (reword, or wait for the cap). */
const NOT_RETRYABLE = new Set(["moderation", "hourly", "daily", "site_cap"])

type Phase = "form" | "painting" | "results" | "error"

/** What one run was asked for; "Try again" repeats it. */
type Request = { prompt: string; style: ArtworkStyleKey; name: string; line: string }
type Run = Request & { id: number | null; bottle: BottleType }

/** What "Use this" hands the editor. */
export type ArtworkChoice = { generationId: number; option: ArtworkOption; name: string; line: string }

/** The server's own normalisation (squish), so the count and the check agree with it. */
const squish = (value: string) => value.replace(/\s+/g, " ").trim()

export function DescribeSheet({
  open,
  onOpenChange,
  styles,
  bottle,
  replaces,
  onUse,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  styles: StudioAi["styles"]
  /** The bottle being designed; a run is painted at that label's aspect. */
  bottle: BottleType
  /** The label already has elements: using an option replaces them. */
  replaces: boolean
  /** Place the chosen option on the label; rejects when it cannot be loaded. */
  onUse: (choice: ArtworkChoice) => Promise<void>
}) {
  const defaultStyle: ArtworkStyleKey = styles.some((s) => s.key === "classic") ? "classic" : (styles[0]?.key ?? "classic")
  const [style, setStyle] = React.useState<ArtworkStyleKey>(defaultStyle)
  const [description, setDescription] = React.useState("")
  const [name, setName] = React.useState("")
  const [line, setLine] = React.useState("")
  const [fieldError, setFieldError] = React.useState<string | null>(null)

  const [phase, setPhase] = React.useState<Phase>("form")
  const [run, setRun] = React.useState<Run | null>(null)
  const [options, setOptions] = React.useState<ArtworkOption[]>([])
  const [error, setError] = React.useState<ArtworkError | null>(null)
  const [slow, setSlow] = React.useState(false)
  const [using, setUsing] = React.useState<number | null>(null)
  const [useError, setUseError] = React.useState<string | null>(null)
  /** Bumped on every run, so the slow-message timer and focus restart with it. */
  const [paintSeq, setPaintSeq] = React.useState(0)

  const controllerRef = React.useRef<AbortController | null>(null)
  const openRef = React.useRef(open)
  openRef.current = open
  const promptRef = React.useRef<HTMLTextAreaElement>(null)
  const paintingRef = React.useRef<HTMLDivElement>(null)
  const resultsRef = React.useRef<HTMLHeadingElement>(null)
  const errorRef = React.useRef<HTMLDivElement>(null)
  const styleRefs = React.useRef(new Map<ArtworkStyleKey, HTMLButtonElement>())
  const styleLabelId = React.useId()
  const optionId = React.useId()

  // A run in flight is abandoned (never cancelled server-side) if the studio unmounts.
  React.useEffect(() => () => controllerRef.current?.abort(), [])

  // After a minute, say so and offer another attempt.
  React.useEffect(() => {
    if (phase !== "painting") return
    const timer = setTimeout(() => setSlow(true), SLOW_AFTER_MS)
    return () => clearTimeout(timer)
  }, [phase, paintSeq])

  // Keyboard and screen-reader users follow the phase: focus moves to what
  // replaced the control they used (the results heading when done).
  React.useEffect(() => {
    if (!openRef.current) return
    const target =
      phase === "painting"
        ? paintingRef.current
        : phase === "results"
          ? resultsRef.current
          : phase === "error"
            ? errorRef.current
            : paintSeq > 0
              ? promptRef.current
              : null
    target?.focus({ preventScroll: true })
  }, [phase, paintSeq])

  const paint = async (request: Request) => {
    controllerRef.current?.abort()
    const controller = new AbortController()
    controllerRef.current = controller
    const live = () => !controller.signal.aborted && controllerRef.current === controller

    setRun({ ...request, id: null, bottle })
    setOptions([])
    setError(null)
    setFieldError(null)
    setUseError(null)
    setSlow(false)
    setPaintSeq((n) => n + 1)
    setPhase("painting")

    try {
      const { id } = await createArtwork({ prompt: request.prompt, style: request.style, bottle_type: bottle }, controller.signal)
      if (!live()) return
      setRun((current) => (current ? { ...current, id } : current))
      const final = await pollArtwork(id, { signal: controller.signal })
      if (!live()) return
      if (final.status === "done" && final.options.length > 0) {
        setOptions(final.options)
        setPhase("results")
      } else {
        setError(final.error ?? { code: "upstream", message: PAINT_FAILED })
        setPhase("error")
      }
    } catch (caught) {
      if (!live()) return
      if (caught instanceof ArtworkRequestError && caught.code === "invalid") {
        // The server's own check (length after normalising): back to the form.
        setFieldError(caught.message)
        setPhase("form")
        return
      }
      setError(
        caught instanceof ArtworkRequestError
          ? { code: caught.code, message: caught.message }
          : { code: "upstream", message: PAINT_FAILED },
      )
      setPhase("error")
    }
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const prompt = squish(description).slice(0, DESCRIPTION_MAX)
    if (prompt.length < DESCRIPTION_MIN) {
      setFieldError(COPY.tooShort)
      promptRef.current?.focus()
      return
    }
    void paint({ prompt, style, name: squish(name), line: squish(line) })
  }

  const tryAgain = () => {
    if (run) void paint({ prompt: run.prompt, style: run.style, name: run.name, line: run.line })
  }

  const backToForm = () => {
    controllerRef.current?.abort()
    setUseError(null)
    setPhase("form")
    // Focus returns to the description (see the focus effect).
    setPaintSeq((n) => n + 1)
  }

  const use = async (option: ArtworkOption) => {
    if (!run?.id || using !== null) return
    setUsing(option.index)
    setUseError(null)
    try {
      await onUse({ generationId: run.id, option, name: run.name, line: run.line })
    } catch (caught) {
      console.error("[studio] painted artwork could not be placed", caught)
      setUseError(COPY.useFailed)
    } finally {
      setUsing(null)
    }
  }

  // Style chips: a radio group with a roving tab stop (arrows move and select).
  const onStyleKeyDown = (event: React.KeyboardEvent) => {
    const keys = styles.map((s) => s.key)
    const i = keys.indexOf(style)
    const next =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? keys[(i + 1) % keys.length]
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? keys[(i - 1 + keys.length) % keys.length]
          : event.key === "Home"
            ? keys[0]
            : event.key === "End"
              ? keys[keys.length - 1]
              : null
    if (!next) return
    event.preventDefault()
    setStyle(next)
    styleRefs.current.get(next)?.focus()
  }

  const count = description.length
  const runBottle = run?.bottle ?? bottle
  const mm = LABEL_MM[runBottle]
  const retryable = error !== null && !NOT_RETRYABLE.has(error.code)

  const announcement =
    phase === "painting" ? (slow ? COPY.slow : COPY.painting) : phase === "results" ? COPY.ready : phase === "error" ? (error?.message ?? "") : ""

  // Two label-shaped mats: warm-white blocks while painting, the options when done.
  const tiles =
    phase === "results"
      ? options.map((option) => (
          <figure key={option.index} className="studio-describe-option">
            <span className="studio-describe-mat">
              <img
                id={`${optionId}-${option.index}`}
                src={option.url}
                alt={`Artwork option ${option.index + 1}`}
                width={option.width}
                height={option.height}
                className="studio-describe-art"
                style={{ aspectRatio: `${option.width} / ${option.height}` }}
              />
            </span>
            <Button
              type="button"
              variant="gold"
              size="sm"
              onClick={() => void use(option)}
              disabled={using !== null}
              aria-describedby={`${optionId}-${option.index}`}
            >
              {using === option.index ? "Placing…" : "Use this"}
            </Button>
          </figure>
        ))
      : [0, 1].map((i) => (
          <div key={i} className="studio-describe-option" aria-hidden="true">
            <span className="studio-describe-mat">
              <span className="studio-describe-art is-waiting" style={{ aspectRatio: `${mm.width} / ${mm.height}` }} />
            </span>
          </div>
        ))

  return (
    <StudioSheet open={open} onOpenChange={onOpenChange} title={COPY.title} description={COPY.lede}>
      <div className="studio-panel studio-describe">
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {announcement}
        </div>

        {phase === "form" && (
          <form className="studio-describe-form" onSubmit={submit} noValidate>
            <div>
              <div className="field-label" id={styleLabelId}>
                Style
              </div>
              <div
                className="studio-chips studio-describe-styles"
                role="radiogroup"
                aria-labelledby={styleLabelId}
                onKeyDown={onStyleKeyDown}
              >
                {styles.map((s) => (
                  <button
                    key={s.key}
                    ref={(el) => {
                      if (el) styleRefs.current.set(s.key, el)
                      else styleRefs.current.delete(s.key)
                    }}
                    type="button"
                    role="radio"
                    aria-checked={style === s.key}
                    tabIndex={style === s.key ? 0 : -1}
                    className="studio-chip"
                    onClick={() => setStyle(s.key)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="describe-prompt" className="field-label">
                Describe the artwork
              </label>
              <Textarea
                id="describe-prompt"
                ref={promptRef}
                rows={3}
                maxLength={DESCRIPTION_MAX}
                value={description}
                placeholder={COPY.placeholder}
                onChange={(event) => {
                  setDescription(event.target.value)
                  if (fieldError) setFieldError(null)
                }}
                aria-invalid={Boolean(fieldError)}
                aria-describedby={fieldError ? "describe-prompt-error describe-prompt-count" : "describe-prompt-count"}
                className="form-control-cellar studio-textarea"
              />
              <div className="studio-describe-meta">
                {fieldError ? (
                  <div id="describe-prompt-error" className="field-error">
                    {fieldError}
                  </div>
                ) : (
                  <span />
                )}
                <span id="describe-prompt-count" className="studio-describe-count">
                  {count} / {DESCRIPTION_MAX}
                  <span className="sr-only"> characters</span>
                </span>
              </div>
            </div>

            <div>
              <div className="studio-describe-wording">
                <div>
                  <label htmlFor="describe-name" className="field-label">
                    Name on the label
                  </label>
                  <Input
                    id="describe-name"
                    maxLength={NAME_MAX}
                    value={name}
                    placeholder="Anna & James"
                    onChange={(event) => setName(event.target.value)}
                    aria-describedby="describe-wording-hint"
                    className="form-control-cellar"
                  />
                </div>
                <div>
                  <label htmlFor="describe-line" className="field-label">
                    Occasion line
                  </label>
                  <Input
                    id="describe-line"
                    maxLength={LINE_MAX}
                    value={line}
                    placeholder="Our wedding · 2026"
                    onChange={(event) => setLine(event.target.value)}
                    aria-describedby="describe-wording-hint"
                    className="form-control-cellar"
                  />
                </div>
              </div>
              <div id="describe-wording-hint" className="field-hint">
                {COPY.wordingHint}
              </div>
            </div>

            <Button type="submit" variant="gold" size="lg">
              Paint my artwork
            </Button>
            <div className="field-hint">{COPY.smallPrint}</div>
          </form>
        )}

        {phase === "painting" && (
          <div className="studio-describe-painting" ref={paintingRef} tabIndex={-1}>
            <div className="studio-describe-progress" role="progressbar" aria-label="Painting your artwork">
              <span />
            </div>
            <p>{COPY.painting}</p>
            {slow && (
              <div className="studio-describe-slow">
                <p>{COPY.slow}</p>
                <Button type="button" variant="ghost-hairline" size="sm" onClick={tryAgain}>
                  Try again
                </Button>
              </div>
            )}
            <div className="studio-describe-results">{tiles}</div>
          </div>
        )}

        {phase === "results" && (
          <div className="studio-describe-done">
            <h3 ref={resultsRef} tabIndex={-1} className="studio-describe-heading">
              {COPY.results}
            </h3>
            <div className="studio-describe-results">{tiles}</div>
            {useError && (
              <div className="field-error" role="alert">
                {useError}
              </div>
            )}
            {replaces && <div className="field-hint">{COPY.replaces}</div>}
            <div className="studio-describe-actions">
              <Button type="button" variant="ghost-hairline" size="sm" onClick={tryAgain} disabled={using !== null}>
                Try again
              </Button>
              <button type="button" className="studio-quiet" onClick={backToForm} disabled={using !== null}>
                Change the description
              </button>
            </div>
            <div className="field-hint">{COPY.smallPrint}</div>
          </div>
        )}

        {phase === "error" && error && (
          <div className="studio-describe-error" ref={errorRef} tabIndex={-1}>
            <p>{error.message}</p>
            <div className="studio-describe-actions">
              {retryable && (
                <Button type="button" variant="gold" size="sm" onClick={tryAgain}>
                  Try again
                </Button>
              )}
              <Button type="button" variant="ghost-hairline" size="sm" onClick={backToForm}>
                Change the description
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudioSheet>
  )
}
